import { useInjection } from 'libs/uikit/ioc/React';
import {
  AlertBar,
  Anchor,
  ArrowRightIcon,
  Button,
  Checkbox,
  ExclamationTriangleIcon,
  Flex,
  Form,
  Text,
  TextInput,
  fieldPath,
} from 'libs/uikit/lib';
import { colors } from 'libs/uikit/lib/Color/Colors';
import { useAsyncEvent } from 'libs/uikit/lib/Hooks/useAsyncEvent';
import { errorText } from 'libs/uikit/lib/Text';
import React from 'react';
import { Link } from 'react-router-dom';
import * as z from 'zod';
import { IAuthenticationStore } from '../../core/Abstractions/IAuthenticationStore';

const schema = z.object({
  username: z.string(),
  password: z.string(),
  rememberMe: z.boolean().optional(),
});

interface LoginFormProps {
  onSuccess: () => void;
}

export function LoginForm(props: LoginFormProps) {
  const authenticationStore = useInjection(IAuthenticationStore);

  const onSubmit = useAsyncEvent(async (form: z.infer<typeof schema>) => {
    await authenticationStore.loginLegacyResourceOwnerPassword(form, form.rememberMe);
    props.onSuccess();
  });

  return (
    <Form schema={schema} onSubmit={onSubmit.callback}>
      <Flex gap={1.5} align="stretch">
        <TextInput name={fieldPath(schema, 'username')} label="username" tabIndex={1} />

        <TextInput
          name={fieldPath(schema, 'password')}
          label="password"
          type="password"
          tabIndex={2}
          tip={
            <Text scale={-1} muted>
              <Anchor as={Link} to="/account/forgot-password" color={colors.blue_900}>
                Forgot password?
              </Anchor>
            </Text>
          }
        />

        {!!onSubmit.error && (
          <AlertBar
            intent="warning"
            title={errorText(onSubmit.error, 'The user name or password is incorrect.')}
            icon={ExclamationTriangleIcon}
            onDismiss={() => onSubmit.dismissError()}
          />
        )}

        <Flex gap={1} justify="space-between" align="center" horizontal>
          <Checkbox name={fieldPath(schema, 'rememberMe')} label="Remember me" tabIndex={3} />
          <Button type="submit" primary scale={{ xs: 1, lg: 2 }} iconRight={ArrowRightIcon} tabIndex={4}>
            Login
          </Button>
        </Flex>
      </Flex>
    </Form>
  );
}
